import type { DetailPaneMailLoadErrorDetails } from "owa-personal-os-types";

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | undefined {
    return typeof value === "object" && value !== null ? (value as UnknownRecord) : undefined;
}

function asString(value: unknown): string | undefined {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

function getResponseMessage(record: UnknownRecord): UnknownRecord | undefined {
    const body = asRecord(record.Body);
    const responseMessages = asRecord(body?.ResponseMessages);
    const items = responseMessages?.Items;
    return Array.isArray(items) ? asRecord(items[0]) : undefined;
}

function getMessageTextFromDiagnostic(value: unknown): string | undefined {
    const diagnosticMessage = asString(value);
    const prefix = "MessageText= ";
    return diagnosticMessage?.startsWith(prefix)
        ? diagnosticMessage.slice(prefix.length)
        : undefined;
}

function getResponseCodeFromMessage(value: unknown): string | undefined {
    const message = asString(value);
    const match = message?.match(/(?:^|,\s*)ResponseCode=([^,\s]+)/);
    return match?.[1];
}

function parseCandidate(value: unknown): DetailPaneMailLoadErrorDetails {
    const record = asRecord(value);
    if (!record) {
        return {};
    }
    
    const responseMessage = getResponseMessage(record);
    const additionalInfo = asRecord(record.additionalInfo);
    return {
        errorName: value instanceof Error ? value.name : asString(record.name),
        errorMessage: value instanceof Error ? value.message : asString(record.message),
        messageText: asString(responseMessage?.MessageText) ??
            asString(record.MessageText) ??
            asString(record.messageText) ??
            asString(additionalInfo?.messageText) ??
            getMessageTextFromDiagnostic(additionalInfo?.diagnosticMessage),
        responseCode: asString(responseMessage?.ResponseCode) ??
            asString(record.ResponseCode) ??
            asString(record.responseCode) ??
            asString(additionalInfo?.responseCode) ??
            getResponseCodeFromMessage(record.message),
    };
}

export function getMailLoadErrorDetails(error: unknown): DetailPaneMailLoadErrorDetails {
    const record = asRecord(error);
    const graphQLErrors = Array.isArray(record?.graphQLErrors) ? record.graphQLErrors : [];
    const graphQLErrorRecords = graphQLErrors.map(asRecord);
    const candidates = [
        error,
        record?.networkError,
        record?.originalError,
        record?.cause,
        record?.extensions,
        asRecord(record?.extensions)?.originalError,
        ...graphQLErrors,
        ...graphQLErrorRecords.map(graphQLError => graphQLError?.originalError),
        ...graphQLErrorRecords.map(graphQLError => graphQLError?.extensions),
        ...graphQLErrorRecords.map(graphQLError => asRecord(graphQLError?.extensions)?.originalError),
    ];
    
    const result: DetailPaneMailLoadErrorDetails = {};
    for (const candidate of candidates) {
        const parsed = parseCandidate(candidate);
        result.errorName ??= parsed.errorName;
        result.errorMessage ??= parsed.errorMessage;
        result.messageText ??= parsed.messageText;
        result.responseCode ??= parsed.responseCode;
    }
    return result;
}
